import { SYNC_REASON_CODES } from './fabricationOrderAlignmentCell'

/**
 * S70 (historia 108) — qué se le dice al usuario cuando el resync que él pidió termina.
 *
 * ⚠️ ESTE FICHERO EXISTE PORQUE EL CONTRATO CAMBIÓ A MITAD DE CICLO. La primera versión de
 * `FabricationOrderAlignmentRest` devolvía 200 sin cuerpo, y esta ventana solo podía decir "se
 * encoló". Sun publicó después el resultado, con el mismo `SyncOutcomeDto` del envío en bloque, y su
 * propio comentario explica por qué (`StandardFabricationOrderResyncService:95-99`): *"the queue
 * never brings a verdict back; it is the right transport for the automatic alignment, where nobody
 * is waiting, and the wrong one for a button somebody pressed"*. Con eso, la cláusula del resultado
 * PARCIAL de S70 pasa de no implementable a implementada.
 *
 * Tres desenlaces y solo tres, y NINGUNO se presenta como los otros:
 *   · `DELIVERED`      se aplicó entera en el otro nodo
 *   · `REJECTED` + `RESYNC_DEGRADED`  solo la cabecera, porque allí hay piezas en curso.
 *     NO es un fallo -- algo se alineó -- y no puede leerse como si lo fuera
 *   · `NOT_DELIVERED`  el nodo no contesta, la cola lo tiene y terminará solo. Tampoco es un fallo
 *
 * Un `REJECTED` por cualquier otro motivo sí lo es, y se muestra con su código estable, que es la
 * clave i18n (R10′): nunca con `reasonMessage`, que es diagnóstico para desarrollo.
 */

/**
 * Los códigos de rechazo que este cliente sabe traducir: los 12 motivos más los del propio resync.
 *
 * ⚠️ JUP-12 del Judge (vuelta 3). Aquí NO vale la regla del indicador. `SyncOutcomeDto.of` publica
 * `outcome.getReasonCode()` **tal cual**, sin pasar por `screenCodeOf`, así que a esta alerta llega el
 * código DE CABLE: puede ser uno de los 12, pero también `TARGET_REJECTED` -- el comodín de
 * `TargetRejectedFabricationOrder` cuando el 409 del destino no trae un código identificable --, que
 * NO está traducido. Sin esta guarda el usuario veía el identificador en crudo: exactamente la
 * bandera JUP-2, que yo mismo había cerrado en el `catch` de la acción y no aquí.
 *
 * Un código que no está en la lista no se enseña: la alerta se queda con su título, que ya dice que
 * el resync fue rechazado. Decir menos antes que enseñar un identificador.
 *
 * ⚠️ Y por eso hay un mapa `CABLE_TO_SCREEN`. Sun mantiene DOS vocabularios a propósito
 * (`contrato-sun.md` §4, corregido el 2026-09-10): el transporte lleva el código de cable porque
 * `SyncOutcomeDto` lo comparte el envío en bloque de la parte 1, y mapearlo allí cambiaría en
 * silencio el contrato de algo ya entregado. Sin el mapa, un rechazo por producción en curso
 * (`REPLICA_NOT_DELETABLE`) caería fuera de la lista y la alerta se quedaría MUDA sobre el motivo más
 * probable de un resync rechazado, teniendo la frase ya traducida bajo su código de pantalla. El mapa
 * es el espejo de `SyncEventReason.ofRejectionCode`; si Sun añade una pareja y aquí no, ese código
 * simplemente no se muestra, que es el fallo benigno.
 */
const CABLE_TO_SCREEN = {
  FABRICATION_ORDER_ALREADY_SENT_TO_NODE: 'ALREADY_SENT',
  STALE_UPDATE_DISCARDED: 'STALE_DISCARDED',
  REPLICA_NOT_DELETABLE: 'DELETION_REFUSED_PRODUCTION',
  LOCAL_FABRICATION_ORDER_CODE_CONFLICT: 'CODE_COLLISION'
}

export const TRANSLATABLE_RESYNC_CODES = [
  ...SYNC_REASON_CODES,
  'CLUSTER_ALIGNMENT_NOT_ENABLED',
  'NEVER_SYNCED_WITH_NODE',
  'FABRICATION_ORDER_NOT_FOUND',
  'TARGET_NODE_REQUIRED',
  'INVALID_TARGET_NODE',
  'TARGET_NODE_UNREACHABLE',
  // JUP-15: los dos que puede devolver EL RECEPTOR al aplicar el paquete. Están traducidos en los 8
  // idiomas desde la parte 1, así que dejarlos fuera hacía que la alerta se callara teniendo la frase
  // ya escrita. `LIFECYCLE_ROLLBACK_REFUSED` NO se añade: no tiene clave, y añadirlo aquí sin
  // traducirlo enseñaría el identificador, que es lo que esta lista existe para impedir.
  'MISSING_DEPENDENCY_IN_TARGET',
  'INCOMPLETE_FABRICATION_ORDER_PACKAGE'
]

const DELIVERED = 'DELIVERED'
const REJECTED = 'REJECTED'
const NOT_DELIVERED = 'NOT_DELIVERED'
// Ya no lo produce el envío (`SyncOutcome.State:33-35`, "kept so a client written against it keeps
// compiling"), pero una respuesta vieja o un nodo sin actualizar puede traerlo. Se trata como lo que
// significa: aún no hay veredicto.
const ACCEPTED = 'ACCEPTED'

// ⚠️ EL MISMO codigo que el motivo del indicador, no uno propio del resync: Sun unifico los dos
// el 2026-09-09 bajo su convencion UN HECHO, UNA CLAVE, con tests que fallan si alguien los
// separa. Aqui se nota en que la celda de la columna y esta alerta dicen ya lo mismo.
const DEGRADED = 'RESYNC_DEGRADED'

/**
 * La clave con la que se traduce un código de rechazo, o cadena vacía si este cliente no lo conoce.
 *
 * @param {string} reasonCode Código tal y como llega del transporte
 * @returns {string} Clave i18n, o '' si no se puede mostrar
 */
const screenCodeOf = (reasonCode) => {
  const code = CABLE_TO_SCREEN[reasonCode] || reasonCode
  return TRANSLATABLE_RESYNC_CODES.includes(code) ? code : ''
}

/**
 * The alert of a finished resync.
 *
 * @param {Object} outcome Answer of the resync route (`SyncOutcomeDto`)
 * @param {Function} t_ Translation function of the window
 * @returns {Object} Alert payload: `{ severity, title, message }`
 */
export const buildResyncAlert = (outcome, t_) => {
  const node = outcome?.targetNodeName || ''
  const code = outcome?.fabricationOrderCode || ''
  const state = outcome?.state

  // Defensivo, y por un defecto real de la parte 1: una respuesta que no es la esperada NO puede
  // presentarse como éxito. Callarse y decir que salió bien es la peor de las dos mentiras.
  if (state === undefined || state === null || state === '') {
    return {
      severity: 'error',
      title: t_('The resynchronisation gave no answer'),
      message: t_('The other node did not report what happened to {{code}}. Check its alignment.', { code })
    }
  }

  if (state === DELIVERED) {
    return {
      severity: 'success',
      title: t_('Resynchronised'),
      message: t_('{{code}} is now aligned with {{node}}.', { code, node })
    }
  }

  // El parcial. Es el desenlace que S70 nombra por su nombre, y va en `warning` a propósito: no es
  // un error (la cabecera SÍ se alineó) ni un éxito (el workflow no viajó).
  if (state === REJECTED && outcome?.reasonCode === DEGRADED) {
    return {
      severity: 'warning',
      title: t_('Only the header was aligned'),
      message: t_('{{node}} has parts under way for {{code}}, so its workflow and materials were left untouched.', { code, node })
    }
  }

  if (state === NOT_DELIVERED || state === ACCEPTED) {
    return {
      severity: 'warning',
      title: t_('The resynchronisation is on its way'),
      message: t_('{{node}} is not answering, so {{code}} was queued and will be aligned when it comes back.', { code, node })
    }
  }

  const reason = outcome?.reasonCode || ''
  return {
    severity: 'error',
    title: t_('The resynchronisation was refused'),
    message: screenCodeOf(reason) === '' ? '' : t_(screenCodeOf(reason))
  }
}

export default buildResyncAlert
